import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { TEMPLATE_KINDS, TEMPLATE_PLACEHOLDERS } from '@/shared/constants';
import { committed, logActivity } from '@/shared/data';

export const KIND_USES = {
  Credentials: 'Send credentials on an order',
  Invoice: 'Send invoice on an order',
  Expiry: 'Expiry reminders on the dashboard',
  Winback: 'Win-back reminders on the dashboard',
};

/** Made-up order values for previews. */
export const SAMPLE_VARS = {
  name: 'Maya',
  code: 'GC-4KX9QZ',
  server: 'http://line.ghostcable.app:8080',
  username: 'maya_okafor',
  password: 'w7Rk2pQe',
  plan: 'Demon · 1 Year · 2 connections',
  amount: '$56.00',
  method: 'Bitcoin',
  ends: '2 November 2027',
  discount: 'BACK15-MAYA',
};

/** Placeholders in a text that the app will not fill. */
export function unknownPlaceholders(text) {
  const found = String(text ?? '').match(/\{\w+\}/g) ?? [];
  return [...new Set(found)].filter((t) => !TEMPLATE_PLACEHOLDERS.includes(t));
}

function fromTemplate(t) {
  return {
    Name: t?.Name ?? '',
    kind_c: t?.kind_c || 'Credentials',
    subject_c: t?.subject_c ?? '',
    body_c: t?.body_c ?? '',
  };
}

/** Add or edit an email template. Placeholders in braces are filled from the order. */
export default function TemplateDialog({ open, onOpenChange, template, onSaved }) {
  const editing = !!template;
  const [form, setForm] = useState(fromTemplate(template));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(fromTemplate(template));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  function insert(token) {
    setForm((f) => {
      const body = f.body_c;
      const gap = body && !/\s$/.test(body) ? ' ' : '';
      return { ...f, body_c: `${body}${gap}${token}` };
    });
  }

  const unknown = unknownPlaceholders(`${form.subject_c} ${form.body_c}`);

  async function submit() {
    if (!form.Name.trim()) {
      toast.error('Name the template');
      return;
    }
    if (!form.subject_c.trim()) {
      toast.error('Add the email subject');
      return;
    }
    if (!form.body_c.trim()) {
      toast.error('Write the email body');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      kind_c: form.kind_c,
      subject_c: form.subject_c.trim(),
      body_c: form.body_c.trim(),
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('templates_c').update({ Id: template.Id, ...fields })
        : await sdk.table('templates_c').create(fields);
      const saved = committed(res, editing ? 'Template saved' : 'Template added');
      if (!saved) return;
      logActivity('Email', editing ? 'Template edited' : 'Template added', fields.Name, fields.kind_c);
      onOpenChange(false);
      setForm(fromTemplate(null));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      wide
      title={editing ? 'Edit template' : 'New template'}
      description="Words in braces are swapped for the order's details when you send it."
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Add template'}
      busyLabel={editing ? 'Saving…' : 'Adding…'}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Template name" htmlFor="tp-name">
          <Input id="tp-name" value={form.Name} onChange={text('Name')} placeholder="Line credentials" />
        </Field>
        <Field label="Used for" htmlFor="tp-kind" hint={KIND_USES[form.kind_c]}>
          <PickSelect id="tp-kind" value={form.kind_c} onChange={set('kind_c')} values={TEMPLATE_KINDS} placeholder="Choose where it is used" />
        </Field>
      </div>
      <Field label="Email subject" htmlFor="tp-subject">
        <Input id="tp-subject" value={form.subject_c} onChange={text('subject_c')} placeholder="Your Ghost Cable line is live ({code})" />
      </Field>
      <Field label="Email body" htmlFor="tp-body">
        <Textarea
          id="tp-body"
          rows={9}
          value={form.body_c}
          onChange={text('body_c')}
          placeholder={'Hi {name},\n\nYour line for order {code} is ready.'}
          className="font-mono text-sm"
        />
      </Field>
      <div>
        <p className="text-xs text-muted-foreground">Add a placeholder to the end of the body:</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {TEMPLATE_PLACEHOLDERS.map((token) => (
            <button
              key={token}
              type="button"
              onClick={() => insert(token)}
              className="rounded-sm border border-border bg-muted px-2 py-1 font-mono text-xs text-muted-foreground transition-colors hover:border-primary/60 hover:text-foreground"
            >
              {token}
            </button>
          ))}
        </div>
        {unknown.length > 0 && (
          <p className="mt-2 text-xs text-destructive">
            {unknown.join(', ')} {unknown.length === 1 ? 'is' : 'are'} not filled in by the app and will be sent as written.
          </p>
        )}
      </div>
    </FormDialog>
  );
}
